import { api } from "../base";

export interface IHarukoAdditionalDataField {
  objectIdType?: string;
  category?: string;
  key?: string;
  value?: string;
}

export interface IHarukoCollateralEntry {
  asset: string;
  amount: string;
  haircut: string;
}

export interface IHarukoCollateralSnapshot {
  timestamp: number;
  entries: IHarukoCollateralEntry[];
}

export interface IHarukoThreshold {
  thresholdName: string;
  threshold: string;
}

export interface IHarukoInterestRate {
  timestamp: number;
  rate: string;
}

export interface IHarukoValuationCollateral {
  asset: string;
  amount: string;
  haircut: string;
  value: string;
}

export interface IHarukoLoanValuation {
  paymentsStatus: string;
  loanMatured: boolean;
  side: string;
  accountId: number;
  accountName: string;
  counterpartyId: number;
  counterpartyName: string;
  liveLtv: string;
  ltvDelta: string;
  liveClr: string;
  clrDelta: string;
  paymentsAssetRefPx: string;
  dailyAccrual: string;
  remainingAccrualDays: number;
  interestPayable: string;
  interestDue: string;
  loanValue: string;
  collateralValue: string;
  collateralValueWithHaircut: string;
  collateral: IHarukoValuationCollateral[];
  collateralStatus: string;
}

export interface IHarukoLoan {
  loanId: string;
  loanRecordId: number;
  active: boolean;
  additionalDataFields?: IHarukoAdditionalDataField[];
  loanAsset: string;
  loanAmount: string;
  collateral: IHarukoCollateralSnapshot[];
  interestType: string;
  businessDayConvention: string;
  dayCountConvention: string;
  interestRates: IHarukoInterestRate[];
  paymentAsset: string;
  paymentFrequency: string;
  assetPx: string;
  autoPayments: boolean;
  term: string;
  accrualStartTs: number;
  accrualEndTs: number;
  accrualEndInclusive: boolean;
  loanOriginationTs: number;
  maturityTs: number;
  principalInceptionTs: number;
  principalMaturityTs: number;
  lastPaymentDueTs: number;
  comments: string;
  externalId: string;
  loanLinkId: string;
  borrowerType: string;
  borrowerId: number;
  borrowerName: string;
  borrowerPrincipalAccountId: number;
  borrowerPrincipalAccountName: string;
  borrowerPaymentsAccountId: number;
  borrowerPaymentsAccountName: string;
  lenderType: string;
  lenderId: number;
  lenderName: string;
  lenderPrincipalAccountId: number;
  lenderPrincipalAccountName: string;
  lenderPaymentsAccountId: number;
  lenderPaymentsAccountName: string;
  feeType: string;
  feeAsset: string;
  borrowerFee: string;
  lenderFee: string;
  borrowerFeeAccountId: number;
  borrowerFeeAccountName: string;
  lenderFeeAccountId: number;
  lenderFeeAccountName: string;
  riskRatioType: string;
  initialLtv: string;
  initialClr: string;
  thresholds: IHarukoThreshold[];
  valuation: IHarukoLoanValuation;
}

interface HarukoLoansResponse {
  status: string;
  result: { loans: IHarukoLoan[] };
}

interface HarukoLoanResponse {
  status: string;
  result: { loan: IHarukoLoan };
}

export interface IHarukoVenueAccount {
  id: number;
  name: string;
  strategyMode?: string;
  account?: string;
  venueType: string;
  archived: boolean;
  venue: string;
}

export interface IHarukoVenueAccountGroup {
  group: string;
  groupId: number;
  venues: IHarukoVenueAccount[];
}

interface HarukoVenueAccountsResponse {
  status: string;
  result: { groups: IHarukoVenueAccountGroup[] };
}

export interface IHarukoCreateLoanRequest {
  loan: Partial<IHarukoLoan>;
}

export interface IHarukoUpdateLoanRequest {
  loan: Partial<IHarukoLoan>;
}

const loansEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    getHarukoLoans: build.query<IHarukoLoan[], void>({
      query: () => ({
        url: "/api/trading-ops/v1/haruko-loans",
      }),
      transformResponse: (response: HarukoLoansResponse) =>
        response?.result?.loans ?? [],
      providesTags: ["HarukoLoans"],
    }),

    getHarukoLoan: build.query<IHarukoLoan | null, string>({
      query: (loanId) => ({
        url: "/api/trading-ops/v1/haruko-loan",
        params: { loanId },
      }),
      transformResponse: (response: HarukoLoanResponse) =>
        response?.result?.loan ?? null,
      providesTags: (_result, _error, loanId) => [
        { type: "HarukoLoans", id: loanId },
      ],
    }),

    getHarukoVenueAccounts: build.query<IHarukoVenueAccountGroup[], void>({
      query: () => ({
        url: "/api/trading-ops/v1/haruko-venue-accounts",
      }),
      transformResponse: (response: HarukoVenueAccountsResponse) =>
        response?.result?.groups ?? [],
    }),

    createHarukoLoan: build.mutation<
      IHarukoLoan | null,
      IHarukoCreateLoanRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/haruko-loan",
        method: "POST",
        body,
      }),
      transformResponse: (response: HarukoLoanResponse) =>
        response?.result?.loan ?? null,
      invalidatesTags: ["HarukoLoans"],
    }),

    updateHarukoLoan: build.mutation<
      IHarukoLoan | null,
      IHarukoUpdateLoanRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/haruko-loan",
        method: "PUT",
        body,
      }),
      transformResponse: (response: HarukoLoanResponse) =>
        response?.result?.loan ?? null,
      invalidatesTags: (_result, _error, { loan }) =>
        loan.loanId
          ? [{ type: "HarukoLoans", id: loan.loanId }, "HarukoLoans"]
          : ["HarukoLoans"],
    }),
  }),
});

export const {
  useGetHarukoLoansQuery,
  useGetHarukoLoanQuery,
  useGetHarukoVenueAccountsQuery,
  useCreateHarukoLoanMutation,
  useUpdateHarukoLoanMutation,
} = loansEndpoints;
